import { useSelector } from 'react-redux';

/**
 * Custom hook for Role-Based Access Control (RBAC).
 * 
 * Uses the Redux store to check if the current user has specific permissions or roles.
 * Implements special logic for 'Super Admin' and 'Admin' roles to have full access
 * if they are 'internal' users and 'active'.
 */
const usePermission = () => {
    const { userInfo } = useSelector((state) => state.auth || {});

    // Helper to safely access nested properties
    const userRole = userInfo?.role?.name || userInfo?.roleName || userInfo?.role;
    const userType = userInfo?.type || userInfo?.userType;
    const permissions = userInfo?.permissions || [];
    const status = userInfo?.status;

    const isSuperAdmin = userRole === 'Super Admin' || userType === 'admin';

    const hasPermission = (permissionName) => {
        if (isSuperAdmin && status === 'active' && userType === 'internal') return true;
        if (!permissionName) return false;
        if (permissions.includes('all') || permissions.includes('*')) return true;
        const [module] = permissionName.split('.');
        if (module && (permissions.includes(`${module}.all`) || permissions.includes(`${module}.*`))) return true;

        // Standard permission check
        return permissions.includes(permissionName);
    };

    /**
     * Check if the user has a specific role.
     * @param {string} roleName - The role name to match.
     * @returns {boolean} - True if user has the role.
     */
    const hasRole = (roleName) => {
        if (!userRole || !roleName) return false;
        if (userRole === roleName) return true;
        // Support 'Asset Management - v2' matching 'Asset Management'
        if (roleName === 'Asset Management' && userRole.startsWith('Asset Management')) return true;
        return false;
    };

    return {
        hasPermission,
        hasRole,
        isSuperAdmin,
        user: userInfo,
        permissions,
        // Aliases for backward compatibility
        can: hasPermission,
        is: hasRole
    };
};

export default usePermission;
